import {useEffect, useState} from 'react';
import {Autocomplete, Button, Group, Radio, Stack} from '@mantine/core';
import {useAnalyzeRepositoryQuery} from '../../pipelines/hooks/usePipelinesQuery';
import {RepositoryAnalysis} from '../../pipelines/types';
import {AnalysisResultPanel} from '../../pipelines/components/AnalysisResultPanel';
import {useCodeRepositories} from '../hooks/useCodeRepositories';
import {CodeRepository} from '../types';
import {PlatformDrawer} from '@flexinet/ui-components';

interface AnalyzeRepositoryDrawerProps {
    opened: boolean;
    onClose: () => void;
    repositoryName?: string;
}

export const AnalyzeRepositoryDrawer = ({
                                            opened,
                                            onClose,
                                            repositoryName: initialRepositoryName,
                                        }: AnalyzeRepositoryDrawerProps) => {

    const [repositoryName, setRepositoryName] = useState(initialRepositoryName ?? '');
    const [branchName, setBranchName] = useState('master');
    const [analysis, setAnalysis] = useState<RepositoryAnalysis | undefined>(undefined);

    const {items: repositories} = useCodeRepositories();
    const repoData = repositories.map((r: CodeRepository) => r.name);

    const {refetch, isFetching: analyzing} = useAnalyzeRepositoryQuery(repositoryName, branchName || undefined);

    useEffect(() => {
        if (opened) {
            setRepositoryName(initialRepositoryName ?? '');
            setBranchName('master');
            setAnalysis(undefined);
        }
    }, [opened, initialRepositoryName]);

    const handleAnalyze = async () => {
        const result = await refetch();
        if (result.data) {
            setAnalysis(result.data);
        }
    };

    const handleReset = () => {
        setAnalysis(undefined);
    };

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title="Analyze Repository"
        >
            {!analysis ? (
                <Stack gap="xl" mt="xl">
                    <Autocomplete
                        label="Code Repository"
                        description="Select the repository to analyze"
                        data={repoData}
                        value={repositoryName}
                        onChange={setRepositoryName}
                        placeholder="Select repository"
                        disabled={!!initialRepositoryName}
                        withAsterisk
                    />
                    <Radio.Group
                        label="Branch Name"
                        description="Select the branch to analyze"
                        value={branchName}
                        onChange={(value) => setBranchName(value)}
                    >
                        <Group mt="xs">
                            <Radio value="master" label="master"/>
                            <Radio value="main" label="main"/>
                        </Group>
                    </Radio.Group>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAnalyze}
                            loading={analyzing}
                            disabled={!repositoryName}
                        >
                            Analyze
                        </Button>
                    </Group>
                </Stack>
            ) : (
                <Stack gap="xl" mt="xl">
                    <AnalysisResultPanel analysis={analysis}/>

                    <Group justify="flex-end">
                        <Button variant="default" onClick={handleReset}>
                            Re-analyze
                        </Button>
                        <Button onClick={onClose}>
                            Close
                        </Button>
                    </Group>
                </Stack>
            )}
        </PlatformDrawer>
    );
};
